import SimpleForm, { SimpleFormItemProps } from "../../SimpleForm";
import dayjs from "dayjs";
import { formatFormName, } from "../util";
import { getInputPlaceholder, getSelectPlaceholder } from '../../utils/ruleUtils';
import { dataToUpload, dataToUploads } from '../../EdiTableCopy/utils';
import { EditVirtualMainTableColumnType } from "../interface";

export function getFormItem (record: any, props?: EditVirtualMainTableColumnType,) {
  if (!props) return null;
  const {
    formItemType,
    label,
    name,
    rules,
    disabled,
    required,
    initialValue,
    enumCode,
    relativePathName,
    form,
    rowIndex,
    inputProps,
    inputNumberProps,
    selectProps,
    superSelectProps,
    selectTableProps,
    superSelectTableProps,
    switchProps,
    textAreaProps,
    uploadProps,
    uploadImageProps,
    uploadImagesProps,
    checkboxGroupProps,
    checkboxProps,
    datePickerProps,
    rangePickerProps,
    timePickerProps,
    afterFormItemProps,
    superAutoCompleteProps,
    associationName,
    optionFieldName,
    formatRules,
  } = props;
  const relativePath = relativePathName ? record[relativePathName] : initialValue;
  const baseSearchFormProps: SimpleFormItemProps = {
    name: formatFormName(record.key, name),
    initialValue,
    form,
    rules: formatRules ? formatRules({...record, ...form?.getFieldsValue(true)}) : rules,
  };
  if (required && !rules) {
    baseSearchFormProps.rules = [
      {
        required,
      },
    ];
  }
  const baseFormProps = { disabled: typeof disabled === 'function' ? disabled({...record, ...form?.getFieldsValue(true)}) : disabled, };
  switch (formItemType) {
    case 'Input':
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{
            ...baseFormProps,
            ...inputProps,
            placeholder: getInputPlaceholder(label, inputProps, baseSearchFormProps?.rules),
          }}
        />
      );
    case 'InputNumber':
      return (
        <SimpleForm.InputNumber
          {...baseSearchFormProps}
          inputNumberProps={{
            ...baseFormProps,
            ...inputNumberProps,
            placeholder: getInputPlaceholder(label, inputNumberProps, baseSearchFormProps?.rules),
          }}
        />
      );
    case 'SuperAutoComplete':
      return (
        <SimpleForm.SuperAutoComplete
          {...baseSearchFormProps}
          superAutoCompleteProps={{
            ...baseFormProps,
            ...superAutoCompleteProps,
            placeholder: getInputPlaceholder(label, superAutoCompleteProps, baseSearchFormProps?.rules),
          }}
        />
      );
    case 'Select':
      return (
        <SimpleForm.Select
          {...baseSearchFormProps}
          enumCode={enumCode}
          selectProps={{
            ...baseFormProps,
            ...selectProps,
            placeholder: getSelectPlaceholder(label, selectProps, baseSearchFormProps?.rules),
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName as string, undefined);
              selectProps?.onChange?.(value, item)
            },
          }}
        />
      );
    case 'SuperSelect':
      return (
        <SimpleForm.SuperSelect
          {...baseSearchFormProps}
          enumCode={enumCode}
          superSelectProps={{
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            ...baseFormProps,
            ...superSelectProps,
            options: superSelectProps?.options || (optionFieldName ? record?.[optionFieldName] : undefined),
            placeholder: getSelectPlaceholder(label, superSelectProps, baseSearchFormProps?.rules),
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName as string, undefined);
              superSelectProps?.onChange?.(value, item)
            },
          }}
        />
      );
    case 'SelectTable':
      if (!selectTableProps) return null;
      let initOptions: any[] = [];
      let namekey = props?.namekey;
      let valuekey = props?.valuekey;
      let optionLabel = selectTableProps?.nameKey;
      let optionValue = selectTableProps?.valueKey;
      if (namekey && valuekey && optionLabel && optionValue) {
        const labels = record[namekey];
        const values = record[valuekey];
        if (Array.isArray(values)) {
          values.forEach((v, k) => {
            initOptions.push({
              [optionLabel as string]: labels[k],
              [optionValue as string]: v,
            });
          });
        } else {
          initOptions.push({
            [optionLabel]: record[namekey],
            [optionValue]: record[valuekey],
          });
        }
      }
      return (
        <SimpleForm.SelectTable
          {...baseSearchFormProps}
          selectTableProps={{
            initOptions,
            ...baseFormProps,
            ...selectTableProps,
            placeholder: getSelectPlaceholder(label, selectTableProps, baseSearchFormProps?.rules),
            beforeFetch: (params: any) => {
              const formData = form?.getFieldsValue(true);
              return selectTableProps.beforeFetch?.(params, { ...record, ...formData }, rowIndex);
            },
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName as string, undefined);
              selectTableProps.onChange?.(value, item, form, rowIndex, record,)
            },
          }}
        />
      );
    case 'SuperSelectTable':
      if (!superSelectTableProps) return null;
      let initOptions_1: any[] = [];
      let labelKey_1 = props?.labelKey;
      let valueKey_1 = props?.valueKey || (Array.isArray(name) ? name[0] : name);
      let label_1 = superSelectTableProps?.labelKey;
      let value_1 = superSelectTableProps?.valueKey;
      if (labelKey_1 && valueKey_1 && label_1 && value_1) {
        const labels = record[labelKey_1];
        const values = record[valueKey_1];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions_1.push({
                [label_1 as string]: labels[k],
                [value_1 as string]: v,
              });
            });
          } else {
            initOptions_1.push({
              [label_1]: record[labelKey_1],
              [value_1]: record[valueKey_1],
            });
          }
        }
      }
      return (
        <SimpleForm.SuperSelectTable
          {...baseSearchFormProps}
          superSelectTableProps={{
            disabledBorderDashed: false,
            initOptions: initOptions_1.length ? initOptions_1 : undefined,
            ...baseFormProps,
            ...superSelectTableProps,
            placeholder: getSelectPlaceholder(label, superSelectTableProps, baseSearchFormProps?.rules),
            beforeFetch: (params: any) => {
              const formData = form?.getFieldsValue(true);
              return superSelectTableProps.beforeFetch?.(params, { ...record, ...formData }, rowIndex);
            },
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName as string, undefined);
              superSelectTableProps.onChange?.(value, item, form, rowIndex, record,)
            },
          }}
        />
      );
    case 'Switch':
      return (
        <SimpleForm.Switch
          {...baseSearchFormProps}
          switchProps={{
            ...baseFormProps,
            ...switchProps,
          }}
        />
      );
    case 'TextArea':
      return (
        <SimpleForm.TextArea
          {...baseSearchFormProps}
          textAreaProps={{
            ...baseFormProps,
            ...textAreaProps,
            placeholder: getInputPlaceholder(label, textAreaProps, baseSearchFormProps?.rules),
          }}
        />
      );
    case 'Upload':
      return (
        <SimpleForm.Upload
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadProps={{
            ...baseFormProps,
            ...uploadProps,
          }}
        />
      );
    case 'UploadImage':
      return (
        <SimpleForm.UploadImage
          {...baseSearchFormProps}
          initialValue={dataToUpload(initialValue, relativePath)}
          uploadImageProps={{
            ...baseFormProps,
            ...uploadImageProps,
            wrapperClassName: 'sku-list-upload',
          }}
        />
      );
    case 'UploadImages':
      return (
        <SimpleForm.UploadImages
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadImagesProps={{
            ...baseFormProps,
            ...uploadImagesProps,
          }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SimpleForm.CheckboxGroup
          {...baseSearchFormProps}
          enumCode={enumCode}
          checkboxGroupProps={{
            ...baseFormProps,
            ...checkboxGroupProps,
          }}
        />
      );
    case 'Checkbox':
      return (
        <SimpleForm.Checkbox
          {...baseSearchFormProps}
          checkboxProps={{
            ...baseFormProps,
            ...checkboxProps,
          }}
        />
      );
    case 'DatePicker':
      const datePickerInit = baseSearchFormProps.initialValue;
      if (datePickerInit && !dayjs.isDayjs(datePickerInit)) {
        baseSearchFormProps.initialValue = dayjs(datePickerInit);
      }
      return (
        <SimpleForm.DatePicker
          {...baseSearchFormProps}
          datePickerProps={{
            ...baseFormProps,
            ...datePickerProps,
          }}
        />
      );
    case 'RangePicker':
      const rangePickerInit = baseSearchFormProps.initialValue;
      if (Array.isArray(rangePickerInit)) {
        baseSearchFormProps.initialValue = rangePickerInit.map(item => {
          if (dayjs.isDayjs(item)) return item;
          return dayjs(item);
        });
      } else {
        baseSearchFormProps.initialValue = undefined;
      }
      return (
        <SimpleForm.RangePicker
          {...baseSearchFormProps}
          rangePickerProps={{
            ...baseFormProps,
            ...rangePickerProps,
          }}
        />
      );
    case 'TimePicker':
      const timePickerInit = baseSearchFormProps.initialValue;
      if (timePickerInit && !dayjs.isDayjs(timePickerInit)) {
        baseSearchFormProps.initialValue = dayjs(timePickerInit);
      }
      return (
        <SimpleForm.TimePicker
          {...baseSearchFormProps}
          timePickerProps={{
            ...baseFormProps,
            ...timePickerProps,
          }}
        />
      );
    default:
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{
            ...baseFormProps,
            ...inputProps,
            placeholder: getInputPlaceholder(label, inputProps, baseSearchFormProps?.rules),
          }}
        />
      );
  }
}